import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { IElement } from 'bom/types';
import { FormProvider, useNotificationContext } from 'core/states';
import { mapJobPartNotesToManagedNotes } from 'jobs/components/JobDetails';
import { useJobPartActions } from 'jobs/hooks';
import { IJobPart } from 'jobs/types';
import { PartNumberHelper } from 'parts/helpers';

import { mapUpsertLineData } from './mappers';
import { upsertLineSchema } from './schema';
import { IUpsertLineFormData } from './types';
import { UpsertLineFormFields } from './UpsertLineFormFields';

interface IProps {
  jobId: number;
  jobPart?: IJobPart;
  element?: IElement;
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const UpsertLine = ({
  jobId,
  jobPart,
  element,
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertJobPart } = useJobPartActions();

  const isEdit = useMemo(() => !!jobPart?.id, [jobPart?.id]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return {};
    }

    return {
      part: {
        ...jobPart.partArr,
        number: PartNumberHelper.toLabel(jobPart.partArr?.number),
        externalPartStatus: jobPart.externalPartStatus,
      },
      manufacturer: jobPart.partArr?.manufacturerId
        ? {
            id: jobPart.partArr?.manufacturerId,
            name: jobPart.partArr?.manufacturerName,
          }
        : jobPart?.manufacturerId
        ? {
            id: jobPart.manufacturerId,
            name: jobPart.manufacturerName,
          }
        : null,
      description:
        jobPart.partArr?.descriptionLong || jobPart.partArr?.description || jobPart.componentName,
      quantity: jobPart.quantity,
      uom: jobPart.uom,
      notes: mapJobPartNotesToManagedNotes(
        jobPart.elements,
        jobPart.componentNotes,
        jobPart.elementComponentNotesMap,
        element
      ),
    };
  }, [element, isEdit, jobPart]);

  const onSubmit =
    (reset: UseFormReset<IUpsertLineFormData>) => async (data: IUpsertLineFormData) => {
      const { success } = await upsertJobPart(mapUpsertLineData(data, jobId, jobPart, element));

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);

        onSubmitSuccess?.();
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IUpsertLineFormData>) => {
    reset?.(defaultValues);
    onClose();

    clearNotification();
  };

  return (
    <FormProvider schema={upsertLineSchema()} defaultValues={defaultValues} resetDefaultValues>
      <UpsertLineFormFields
        jobId={jobId}
        jobPart={jobPart}
        element={element}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
